import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useOwnedTalents } from "@/hooks/use-owned-talents";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ShowCallSheet } from "@shared/schema";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ClipboardList,
  Loader2,
  Plus,
  Trash2,
  ExternalLink,
  Link2,
} from "lucide-react";

type EventOfferingLite = { id: string; name: string };

function safeFmtDate(value: string | Date | null | undefined): string {
  if (!value) return "No date";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "No date";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function TalentCallsheetsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const {
    ownedTalents,
    selectedTalentId,
    setSelectedTalentId,
    selectedTalent,
    isLoading: talentLoading,
  } = useOwnedTalents();

  const { data: callSheets = [], isLoading: sheetsLoading } = useQuery<
    ShowCallSheet[]
  >({
    queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(
        `/api/call-sheets?talentId=${encodeURIComponent(selectedTalentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed to load call sheets");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  // Event offerings available for attachment (talent-scoped + workspace-wide).
  const { data: offerings = [] } = useQuery<EventOfferingLite[]>({
    queryKey: ["/api/event-offerings", "forTalent", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(
        `/api/event-offerings?talentId=${encodeURIComponent(selectedTalentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  const offeringName = (id: string | null | undefined) =>
    offerings.find((o) => o.id === id)?.name || "Unknown offering";

  // ── Create dialog ────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    eventDate: "",
    venueName: "",
    venueAddress: "",
    eventOfferingId: "none",
  });

  function resetForm() {
    setForm({
      title: "",
      eventDate: "",
      venueName: "",
      venueAddress: "",
      eventOfferingId: "none",
    });
  }

  const createCallSheet = useMutation({
    mutationFn: async () => {
      if (!selectedTalentId) throw new Error("no talent selected");
      const r = await apiRequest("POST", "/api/call-sheets", {
        talentId: selectedTalentId,
        title: form.title.trim(),
        eventDate: form.eventDate
          ? new Date(form.eventDate).toISOString()
          : null,
        venueName: form.venueName.trim() || null,
        venueAddress: form.venueAddress.trim() || null,
        eventOfferingId:
          form.eventOfferingId !== "none" ? form.eventOfferingId : null,
      });
      return r.json();
    },
    onSuccess: (sheet: any) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
      });
      setCreateOpen(false);
      resetForm();
      toast({ title: "Created", description: "Call sheet created." });
      navigate(`/call-sheet/${sheet.id}`);
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to create call sheet",
        variant: "destructive",
      }),
  });

  const attachOffering = useMutation({
    mutationFn: async (vars: { id: string; eventOfferingId: string | null }) => {
      const r = await apiRequest("PATCH", `/api/call-sheets/${vars.id}`, {
        eventOfferingId: vars.eventOfferingId,
      });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
      });
      toast({ title: "Updated", description: "Offering attachment updated." });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to update offering",
        variant: "destructive",
      }),
  });

  const deleteCallSheet = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/call-sheets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
      });
      toast({ title: "Deleted", description: "Call sheet removed." });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to delete call sheet",
        variant: "destructive",
      }),
  });

  if (talentLoading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="loading-callsheets"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Call Sheets</CardTitle>
            <CardDescription>
              You don&apos;t have a talent profile linked to your account yet.
              Ask an admin to link you to your talent record.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight"
            data-testid="text-callsheets-title"
          >
            Call Sheets
          </h1>
          <p className="text-muted-foreground text-sm">
            Sectioned schedules for {selectedTalent?.name || "your"} events.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ownedTalents.length > 1 && (
            <Select
              value={selectedTalentId ?? ""}
              onValueChange={(v) => setSelectedTalentId(v)}
            >
              <SelectTrigger className="w-[200px]" data-testid="select-talent">
                <SelectValue placeholder="Select talent" />
              </SelectTrigger>
              <SelectContent>
                {ownedTalents.map((t) => (
                  <SelectItem
                    key={t.id}
                    value={t.id}
                    data-testid={`option-talent-${t.id}`}
                  >
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => {
              resetForm();
              setCreateOpen(true);
            }}
            data-testid="button-new-callsheet"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Call Sheet
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {sheetsLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : callSheets.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="text-no-sheets"
            >
              No call sheets yet. Create one to get started.
            </p>
          ) : (
            <div className="space-y-3">
              {callSheets.map((cs) => {
                const offeringId = (cs as any).eventOfferingId as
                  | string
                  | null
                  | undefined;
                return (
                  <div
                    key={cs.id}
                    className="border rounded-lg p-3 space-y-3"
                    data-testid={`card-callsheet-${cs.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate flex items-center gap-2">
                          <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                          {cs.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {safeFmtDate(cs.eventDate as any)}
                          {cs.venueName ? ` • ${cs.venueName}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge
                          variant={
                            cs.status === "published" ? "default" : "outline"
                          }
                          className="text-xs"
                        >
                          {cs.status}
                        </Badge>
                        <Link
                          href={`/call-sheet/${cs.id}`}
                          data-testid={`link-edit-callsheet-${cs.id}`}
                        >
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() => deleteCallSheet.mutate(cs.id)}
                          data-testid={`button-delete-callsheet-${cs.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Event offering:</span>
                      <Select
                        value={offeringId ?? "none"}
                        onValueChange={(v) =>
                          attachOffering.mutate({
                            id: cs.id,
                            eventOfferingId: v === "none" ? null : v,
                          })
                        }
                      >
                        <SelectTrigger
                          className="h-7 w-[220px] text-xs"
                          data-testid={`select-offering-${cs.id}`}
                        >
                          <SelectValue
                            placeholder="Not attached"
                          >
                            {offeringId
                              ? offeringName(offeringId)
                              : "Not attached"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not attached</SelectItem>
                          {offerings.map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Call Sheet</DialogTitle>
            <DialogDescription>
              Create a standalone call sheet for {selectedTalent?.name || "this talent"}.
              You can attach it to an event offering now or later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="cs-title">Title *</Label>
              <Input
                id="cs-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Summer Festival — Main Stage"
                data-testid="input-callsheet-title"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cs-date">Event date</Label>
              <Input
                id="cs-date"
                type="date"
                value={form.eventDate}
                onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                data-testid="input-callsheet-date"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cs-venue">Venue name</Label>
              <Input
                id="cs-venue"
                value={form.venueName}
                onChange={(e) =>
                  setForm({ ...form, venueName: e.target.value })
                }
                data-testid="input-callsheet-venue"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cs-address">Venue address</Label>
              <Input
                id="cs-address"
                value={form.venueAddress}
                onChange={(e) =>
                  setForm({ ...form, venueAddress: e.target.value })
                }
                data-testid="input-callsheet-address"
              />
            </div>
            <div className="space-y-1">
              <Label>Event offering (optional)</Label>
              <Select
                value={form.eventOfferingId}
                onValueChange={(v) =>
                  setForm({ ...form, eventOfferingId: v })
                }
              >
                <SelectTrigger data-testid="select-callsheet-offering">
                  <SelectValue placeholder="Not attached" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not attached</SelectItem>
                  {offerings.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              data-testid="button-cancel-callsheet"
            >
              Cancel
            </Button>
            <Button
              onClick={() => createCallSheet.mutate()}
              disabled={!form.title.trim() || createCallSheet.isPending}
              data-testid="button-save-callsheet"
            >
              {createCallSheet.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
